import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../contexts/AuthContext';

const WINE_TYPES = [
  { value: 'red',       label: 'Red' },
  { value: 'white',     label: 'White' },
  { value: 'rose',      label: 'Rosé' },
  { value: 'sparkling', label: 'Sparkling' },
  { value: 'dessert',   label: 'Dessert' },
  { value: 'fortified', label: 'Fortified' },
];

const PRICE_RANGES = [
  { value: 'budget',    label: 'Budget' },
  { value: 'mid-range', label: 'Mid-range' },
  { value: 'premium',   label: 'Premium' },
  { value: 'luxury',    label: 'Luxury' },
];

const SCALE_LABELS = {
  sweetness: ['Very Dry', 'Dry', 'Off-Dry', 'Semi-Sweet', 'Sweet'],
  acidity:   ['Very Low', 'Low', 'Medium', 'High', 'Very High'],
  body:      ['Very Light', 'Light', 'Medium', 'Full', 'Very Full'],
};

function RangeSlider({ name, label, min, max, onChange }) {
  const labels = SCALE_LABELS[name];
  return (
    <div style={styles.sliderGroup}>
      <div style={styles.sliderHeader}>
        <span style={styles.sliderLabel}>{label}</span>
        <span style={styles.sliderValue}>{labels[min - 1]} – {labels[max - 1]}</span>
      </div>
      <div style={styles.sliderRow}>
        <span style={styles.sliderHint}>Min</span>
        <input
          type="range" min={1} max={5} value={min}
          onChange={(e) => onChange(name + '_min', parseInt(e.target.value))}
          style={styles.slider}
        />
        <span style={styles.sliderHint}>Max</span>
        <input
          type="range" min={1} max={5} value={max}
          onChange={(e) => onChange(name + '_max', parseInt(e.target.value))}
          style={styles.slider}
        />
      </div>
    </div>
  );
}

export default function TasteProfilePanel({ onClose }) {
  const [form, setForm] = useState({
    sweetness_min: 1, sweetness_max: 5,
    acidity_min:   1, acidity_max:   5,
    body_min:      1, body_max:      5,
    price_range:   null,
    types:         [],
    disliked:      '',
  });
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [saved,   setSaved]     = useState(false);
  const [error,   setError]     = useState(null);

  // Load existing preferences
  useEffect(() => {
    api.get('/users/preferences')
      .then(({ data }) => {
        const t = data.taste;
        if (t) {
          setForm({
            sweetness_min: t.preferred_sweetness_min || 1,
            sweetness_max: t.preferred_sweetness_max || 5,
            acidity_min:   t.preferred_acidity_min   || 1,
            acidity_max:   t.preferred_acidity_max   || 5,
            body_min:      t.preferred_body_min      || 1,
            body_max:      t.preferred_body_max      || 5,
            price_range:   t.preferred_price_range   || null,
            types:         t.preferred_types ? t.preferred_types.split(',').filter(Boolean) : [],
            disliked:      t.disliked_flavors || '',
          });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleSlider = useCallback((key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // keep min <= max
      if (key.endsWith('_min') && value > next[key.replace('_min', '_max')]) {
        next[key.replace('_min', '_max')] = value;
      }
      if (key.endsWith('_max') && value < next[key.replace('_max', '_min')]) {
        next[key.replace('_max', '_min')] = value;
      }
      return next;
    });
  }, []);

  const toggleType = useCallback((type) => {
    setForm((prev) => {
      const types = prev.types.includes(type)
        ? prev.types.filter((t) => t !== type)
        : [...prev.types, type];
      return { ...prev, types };
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.patch('/users/preferences', {
        preferred_sweetness_min: form.sweetness_min,
        preferred_sweetness_max: form.sweetness_max,
        preferred_acidity_min:   form.acidity_min,
        preferred_acidity_max:   form.acidity_max,
        preferred_body_min:      form.body_min,
        preferred_body_max:      form.body_max,
        preferred_price_range:   form.price_range || null,
        preferred_types:         form.types,
        disliked_flavors:        form.disliked.trim() || null,
      });
      setSaved(true);
      setTimeout(() => { setSaved(false); if (onClose) onClose(); }, 1200);
    } catch (err) {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={styles.panel}><p style={styles.loadingText}>Loading...</p></div>;

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose && onClose()}>
      <div style={styles.panel}>
        <div style={styles.header}>
          <h3 style={styles.title}>Taste Profile</h3>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <p style={styles.subtitle}>Tell Vinha what you like and she'll personalise every recommendation.</p>

        {/* Sliders */}
        <RangeSlider name="sweetness" label="Sweetness"
          min={form.sweetness_min} max={form.sweetness_max} onChange={handleSlider} />
        <RangeSlider name="acidity" label="Acidity"
          min={form.acidity_min} max={form.acidity_max} onChange={handleSlider} />
        <RangeSlider name="body" label="Body"
          min={form.body_min} max={form.body_max} onChange={handleSlider} />

        {/* Wine types */}
        <div style={styles.section}>
          <span style={styles.sectionLabel}>Wine types I enjoy</span>
          <div style={styles.typeGrid}>
            {WINE_TYPES.map(({ value, label }) => (
              <button
                key={value}
                style={{ ...styles.typeBtn, ...(form.types.includes(value) ? styles.typeBtnActive : {}) }}
                onClick={() => toggleType(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Price range */}
        <div style={styles.section}>
          <span style={styles.sectionLabel}>Typical budget per bottle</span>
          <div style={styles.typeGrid}>
            {PRICE_RANGES.map(({ value, label }) => (
              <button
                key={value}
                style={{ ...styles.typeBtn, ...(form.price_range === value ? styles.typeBtnActive : {}) }}
                onClick={() => setForm((p) => ({ ...p, price_range: p.price_range === value ? null : value }))}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Disliked flavors */}
        <div style={styles.section}>
          <span style={styles.sectionLabel}>Flavors I dislike (optional)</span>
          <input
            type="text"
            placeholder="e.g. very tannic, oaky, overly sweet"
            value={form.disliked}
            onChange={(e) => setForm((p) => ({ ...p, disliked: e.target.value }))}
            style={styles.textInput}
            maxLength={200}
          />
        </div>

        {error && <p style={styles.errorText}>{error}</p>}

        <button
          style={{ ...styles.saveBtn, ...(saving || saved ? styles.saveBtnDisabled : {}) }}
          onClick={handleSave}
          disabled={saving || saved}
        >
          {saved ? 'Saved!' : saving ? 'Saving…' : 'Save preferences'}
        </button>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '16px',
  },
  panel: {
    background: '#1e1e2e', borderRadius: '12px', padding: '24px',
    width: '100%', maxWidth: '440px', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' },
  title:  { margin: 0, fontSize: '1.1rem', color: '#fff', fontWeight: 600 },
  closeBtn: {
    background: 'none', border: 'none', color: '#888', fontSize: '1.1rem',
    cursor: 'pointer', padding: '4px 8px',
  },
  subtitle: { color: '#aaa', fontSize: '0.85rem', margin: '0 0 20px', lineHeight: 1.4 },
  loadingText: { color: '#aaa', textAlign: 'center' },
  sliderGroup: { marginBottom: '18px' },
  sliderHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '6px' },
  sliderLabel: { color: '#ccc', fontSize: '0.85rem', fontWeight: 500 },
  sliderValue: { color: '#a78bfa', fontSize: '0.8rem' },
  sliderRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  sliderHint: { color: '#666', fontSize: '0.7rem', minWidth: '24px' },
  slider: { flex: 1, accentColor: '#a78bfa', cursor: 'pointer' },
  section: { marginBottom: '18px' },
  sectionLabel: { display: 'block', color: '#ccc', fontSize: '0.85rem', fontWeight: 500, marginBottom: '8px' },
  typeGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  typeBtn: {
    padding: '6px 14px', borderRadius: '20px', border: '1px solid #444',
    background: 'transparent', color: '#aaa', fontSize: '0.8rem',
    cursor: 'pointer', transition: 'all 0.15s',
  },
  typeBtnActive: { background: '#a78bfa', borderColor: '#a78bfa', color: '#fff' },
  textInput: {
    width: '100%', padding: '8px 12px', borderRadius: '8px',
    border: '1px solid #444', background: '#2a2a3e', color: '#fff',
    fontSize: '0.85rem', boxSizing: 'border-box',
  },
  errorText: { color: '#f87171', fontSize: '0.8rem', margin: '0 0 12px' },
  saveBtn: {
    width: '100%', padding: '10px', borderRadius: '8px',
    background: '#a78bfa', border: 'none', color: '#fff',
    fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', marginTop: '4px',
  },
  saveBtnDisabled: { background: '#6b5fa0', cursor: 'default' },
};
